/**
 * SuccessMessage component
 * Displays success confirmation after application submission
 * Fires confetti burst on mount using tenant brand colors
 */
import React, { useEffect, useState } from 'react'
import confetti from 'canvas-confetti'
import { useTranslation } from '../../hooks/useTranslation'
import { useTheme } from '../../lib/theme/theme-provider'

interface SuccessMessageProps {
  onClose: () => void
}

export const SuccessMessage: React.FC<SuccessMessageProps> = ({ onClose }) => {
  const [visible, setVisible] = useState(false)
  const { t } = useTranslation()
  const { theme } = useTheme()

  useEffect(() => {
    // Trigger entrance animation
    requestAnimationFrame(() => setVisible(true))

    // Confetti colors from tenant theme, fallback to DevSquad palette
    const colors = theme
      ? [theme.colors.primary, theme.colors.primaryHover, theme.colors.primaryLight, '#FFFFFF']
      : ['#28F5BE', '#1BE0AB', '#43FECB', '#FFFFFF']

    // Fire confetti burst
    const duration = 2500
    const end = Date.now() + duration

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors,
      })
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors,
      })

      if (Date.now() < end) {
        requestAnimationFrame(frame)
      }
    }

    frame()
  }, [theme])

  return (
    <div
      className={`text-center py-8 px-4 transition-all duration-500 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {/* Animated success icon */}
      <div className="flex justify-center mb-6">
        <div
          className={`w-16 h-16 bg-primary/15 rounded-full flex items-center justify-center transition-transform duration-500 ease-out ${
            visible ? 'scale-100' : 'scale-0'
          }`}
        >
          <svg
            className="w-10 h-10 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
              style={{
                strokeDasharray: 30,
                strokeDashoffset: visible ? 0 : 30,
                transition: 'stroke-dashoffset 0.7s ease-out 0.3s',
              }}
            />
          </svg>
        </div>
      </div>

      {/* Success message */}
      <h3 className="text-xl md:text-2xl font-semibold font-heading text-card-text mb-4">
        {t('application.success.title')}
      </h3>

      <p className="text-base text-card-text-secondary mb-4">
        {t('application.success.subtitle')}
      </p>

      {/* Next steps */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6 text-left">
        <p className="text-sm text-card-text">
          <strong>{t('application.success.nextStepsTitle')}</strong>
        </p>
        <p className="text-sm text-card-text-secondary mt-2">
          {t('application.success.nextStepsDescription')}
        </p>
      </div>

      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="
          w-full md:w-auto
          px-8 py-3
          bg-primary text-surface
          rounded-lg
          font-medium
          hover:bg-primary-hover
          transition-colors
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          min-w-[120px] min-h-[44px]
        "
        aria-label={t('application.success.closeAriaLabel')}
      >
        {t('common.actions.close')}
      </button>
    </div>
  )
}
